import React, { useEffect, useRef, useState, useContext } from 'react'
import debounce from 'lodash/debounce'
import uniqBy from 'lodash/uniqBy'
import { get } from '~/utils/api'
import { WebsocketContext } from './WebsocketConn'
import { Channel } from 'phoenix'
import { websocket } from './TicketListWebsocket'
import { useStore } from '~/components/tickets/store'

type Stars = {
  id: number
  colorId: number
}

type Tags = {
  id: number
  name: string
}

type assignedUsers = {
  id: number
  name: string
}
export type View = 'open' | 'todo' | 'closed' | ''

export type Order =
  | 'createdAsc'
  | 'createdDesc'
  | 'activityAsc'
  | 'activityDesc'

export type TicketOnList = {
  number: number
  id: number
  title: string
  firstAnswer: string
  urgent: boolean
  unreaded: boolean
  reportedBy: number
  avatar: string
  added: Date
  dateLastAnswer: Date
  stars?: Stars[]
  tags: Tags[]
  mailAccount: string
  clientEmail: string
  client: {
    id: number
    name: string
  } | null
  addedBy: {
    id: number
    name: string
  }
  assignedUsers: assignedUsers[]
  checked: boolean
  isMine: boolean
  ticketCategory: {
    id: number
    name: string
    icon: string
  } | null
  ticketSubcategory: {
    id: number
    name: string
  } | null
  ticketCategoryStatus: {
    id: number
    name: string
  } | null
}

export type Filter = {
  view: string
  dateFrom: string | null
  dateTo: string | null
  reportedBy: number | string | null
  mailAccount: number | string | null
  tags: string | null
  title: string | null
  order: Order
  searchAnswers: boolean
  urgentFirst: boolean
  unreadFirst: boolean
  category: number | null
  subcategory: number | null
  categoryStatus: number | null
}

type State = {
  tickets: TicketOnList[]
  loadHook: () => void
  loadMore: () => void
  setSearch: (search: string) => void
  loading: boolean
  search: string
  filter: Filter
  setFilter: React.Dispatch<React.SetStateAction<Filter>>
  setItems: React.Dispatch<React.SetStateAction<TicketOnList[]>>
  channel?: Channel
  unreadTickets: number
}

const defaultFilter: Filter = {
  view: 'open',
  dateFrom: null,
  dateTo: null,
  reportedBy: null,
  mailAccount: null,
  tags: null,
  title: null,
  order: 'createdDesc',
  searchAnswers: false,
  urgentFirst: true,
  unreadFirst: true,
  category: null,
  subcategory: null,
  categoryStatus: null,
}

export const TicketListManagerContext = React.createContext<State>({
  tickets: [],
  loadHook: () => {},
  loadMore: () => {},
  setSearch: () => {},
  setFilter: () => {},
  setItems: () => {},
  loading: false,
  search: '',
  filter: defaultFilter,
  unreadTickets: 0,
})

type Props = { children: React.ReactNode }

const TicketListManager: React.FC<Props> = ({ children }) => {
  const wsContext = useContext(WebsocketContext)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState(defaultFilter)
  const [initialized, setInitialized] = useState(false)
  const [items, setItems] = useState<TicketOnList[]>([])
  const [channel, setChannel] = useState<Channel | undefined>()
  const { view } = useStore()
  const [unreadTickets, setUnreadTickets] = useState<number>(0)

  const loadingRef = useRef(loading)
  const searchRef = useRef(search)
  const filterRef = useRef(filter)
  const itemsRef = useRef(items)
  const viewRef = useRef(view)

  loadingRef.current = loading
  filterRef.current = filter
  itemsRef.current = items
  viewRef.current = view

  const download = (params = {}, more = false) => {
    return new Promise<void>(r => {
      setLoading(true)
      loadingRef.current = true

      if (!more) {
        setItems([])
      }

      get<TicketOnList[]>(`tickets/list`, {
        offset: more ? items.length : 0,
        search,
        ...filterRef.current,
        ...params,
      }).then(newItems => {
        if (more) {
          setItems(uniqBy([...items, ...newItems], e => e.id))
        } else {
          setItems(newItems)
        }

        setLoading(false)
        loadingRef.current = false
        r()
      })
    })
  }

  useEffect(() => {
    const channel = wsContext.socket.channel(
      'phone_tickets:' + window.c.campaignId
    )
    setChannel(channel)

    get<number>(`tickets/unread`).then(unread => {
      setUnreadTickets(unread)
    })

    websocket(
      channel,
      setItems,
      itemsRef,
      viewRef,
      searchRef,
      filterRef,
      setUnreadTickets
    )

    channel.join()
  }, [])

  const loadHook = () => {
    useEffect(() => {
      if (initialized) {
        return
      }

      download().then(() => {
        setInitialized(true)
      })
    }, [])
  }

  const [searchDebounce] = useState(() =>
    debounce(search => {
      download({ search })
    }, 500)
  )

  useEffect(() => {
    if (!initialized) {
      return
    }

    searchDebounce(search)
  }, [search])

  const loadMore = () => {
    if (loadingRef.current) {
      return
    }

    download({}, true)
  }

  useEffect(() => {
    if (!initialized) {
      return
    }

    download()
  }, Object.values(filter))

  return (
    <TicketListManagerContext.Provider
      value={{
        tickets: items,
        loadHook,
        loading,
        search,
        loadMore,
        setSearch,
        filter,
        setFilter,
        setItems,
        unreadTickets,
      }}
    >
      {children}
    </TicketListManagerContext.Provider>
  )
}

export default TicketListManager
